import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, CalendarCheck, ChevronDown, ArrowRight, Layers, Sparkles, Check, BookOpen } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useDemo } from "@/components/landing/DemoContext";
import { useAudit } from "@/components/landing/AuditContext";
import { BASES, ADDONS } from "@/config/clinicOS";

const NAV = [
  { label: "Platform", to: "/platform" },
  { label: "KAI", to: "/kai" },
  { label: "Support", to: "/support" },
];

const NEW_VISITOR_LINK = { label: "New here?", to: "/welcome" };
const MEET_CLINICOS_LINK = { label: "Meet Clinic OS", to: "/join-clinicos" };

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const openDemo = useDemo();
  const openAudit = useAudit();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Smart Book-a-Demo: navigate on desktop (lg+), open modal on mobile/tablet.
  const handleBookDemo = () => {
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches) {
      navigate("/book-a-demo");
    } else {
      openDemo();
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // (Dropdown closes on outside-click; mobile menu closes on link tap.)

  const goToAnchor = (anchor, mode) => {
    setProductsOpen(false);
    const scrollAndMode = () => {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (mode) {
        window.dispatchEvent(new CustomEvent("clinic-os:set-mode", { detail: mode }));
      }
    };
    if (location.pathname !== "/") {
      navigate(`/#${anchor}`);
      setTimeout(scrollAndMode, 150);
    } else {
      scrollAndMode();
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full transition-colors duration-200 ${
        scrolled
          ? "bg-background/95 border-b border-border/60 shadow-sm shadow-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" data-testid="logo-link" className="group flex items-center gap-2.5">
            <Logo />
            <span className="font-display text-base font-extrabold tracking-tight sm:text-lg">
              Clinic <span className="text-gradient">OS</span>
            </span>
            <span className="hidden h-3 w-px bg-border sm:inline-block" aria-hidden />
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground sm:inline">
              by ClinicFirst
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {/* Products dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                data-testid="nav-products"
                onClick={() => setProductsOpen((o) => !o)}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  productsOpen
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                }`}
                aria-expanded={productsOpen}
              >
                Products
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${productsOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
              {productsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, x: "-50%" }}
                  animate={{ opacity: 1, y: 0, x: "-50%" }}
                  exit={{ opacity: 0, y: -4, x: "-50%" }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="products-dropdown"
                  className="fixed left-1/2 top-[68px] z-50 w-[min(1040px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-white font-sans shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] dark:bg-[#0b0b14] dark:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)]"
                  style={{ fontFeatureSettings: '"ss01","cv11"' }}
                >
                  {/* Colored top accent bar */}
                  <div className="h-[3px] w-full bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500" />

                  {/* Top strip — minimal */}
                  <div className="flex items-center justify-between border-b border-border/70 px-6 py-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[13px] font-semibold tracking-tight text-foreground">Products</span>
                      <span className="text-[12px] font-normal text-muted-foreground">Pick a base, snap on add-ons.</span>
                    </div>
                    <Link
                      to="/products"
                      onClick={() => setProductsOpen(false)}
                      data-testid="products-dropdown-view-all"
                      className="group inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      View all products
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </div>

                  <div className="grid grid-cols-12 bg-gradient-to-br from-secondary/30 via-transparent to-secondary/20">
                    {/* COL 1 — Bases (compact, colored) */}
                    <div className="col-span-5 border-r border-border/70 p-5">
                      <p className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
                        Bases · pick one
                      </p>
                      <div className="space-y-2">
                        {BASES.map((m) => {
                          const Icon = m.icon;
                          const featured = m.id === "hub-plus";
                          const isActive = location.pathname === `/products/${m.id}`;
                          const personaFor = m.id === "hub"
                            ? "Solo clinics getting organized"
                            : "Growing clinics ready to scale";
                          const previewFeatures = (m.features || []).slice(0, 4).map((f) => f.split(" — ")[0].split(" (")[0]);
                          return (
                            <Link
                              key={m.id}
                              to={`/products/${m.id}`}
                              data-testid={`products-dropdown-${m.id}`}
                              onClick={() => setProductsOpen(false)}
                              className={`group relative block w-full overflow-hidden rounded-xl border px-3.5 py-3 text-left transition-all hover:-translate-y-0.5 ${
                                featured
                                  ? "border-violet-500/30 bg-gradient-to-br from-violet-500/[0.10] via-violet-500/[0.04] to-transparent hover:border-violet-500/55 hover:shadow-md hover:shadow-violet-500/15"
                                  : "border-blue-500/25 bg-gradient-to-br from-blue-500/[0.08] via-blue-500/[0.03] to-transparent hover:border-blue-500/50 hover:shadow-md hover:shadow-blue-500/10"
                              } ${isActive ? "ring-2 ring-offset-2 ring-offset-background ring-foreground/30" : ""}`}
                            >
                              {/* Decorative blurred glow */}
                              <div className={`pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full blur-3xl ${featured ? "bg-violet-500/20" : "bg-blue-500/15"}`} />

                              {/* Corner ribbon for Popular / Viewing */}
                              {(featured || isActive) && (
                                <span className={`absolute right-3 top-3 z-10 rounded-sm px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.1em] text-white shadow-sm ${
                                  isActive ? "bg-foreground" : "bg-violet-500"
                                }`}>
                                  {isActive ? "Viewing" : "Popular"}
                                </span>
                              )}

                              <div className="relative flex items-center gap-3">
                                <span className={`inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${m.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                                  <Icon className="h-[18px] w-[18px]" />
                                </span>
                                <div className="min-w-0 flex-1 pr-16">
                                  <span className="block text-[14px] font-semibold tracking-tight text-foreground">{m.name}</span>
                                  <p className={`mt-0.5 truncate text-[12px] font-medium ${
                                    featured ? "text-violet-600/85 dark:text-violet-300/85" : "text-blue-600/85 dark:text-blue-300/85"
                                  }`}>
                                    For {personaFor.toLowerCase()}
                                  </p>
                                </div>
                              </div>

                              {/* Hover preview — features reveal */}
                              <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out group-hover:grid-rows-[1fr]">
                                <div className="relative overflow-hidden">
                                  <div className="mt-2.5 border-t border-foreground/10 pt-2.5 pl-[56px] pr-16">
                                    <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                                      {previewFeatures.map((f, i) => (
                                        <li key={i} className="flex items-center gap-1.5 text-[11px] font-normal text-foreground/75">
                                          <Check className={`h-3 w-3 flex-none ${featured ? "text-violet-500" : "text-blue-500"}`} />
                                          <span className="truncate">{f}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              <div className="absolute right-3.5 bottom-3 flex items-baseline gap-0.5">
                                <span className="text-[13.5px] font-semibold tracking-tight text-foreground">{m.priceLabel}</span>
                                <span className="text-[10.5px] font-normal text-muted-foreground">/mo</span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>

                      {/* Build CTA — vibrant */}
                      <Link
                        to="/build"
                        data-testid="products-dropdown-byos"
                        onClick={() => setProductsOpen(false)}
                        className="group mt-3 flex w-full items-center gap-3 rounded-xl border border-foreground/15 bg-gradient-to-br from-foreground/[0.06] via-foreground/[0.02] to-transparent px-3 py-2.5 transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-sm dark:from-white/[0.08]"
                      >
                        <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-sm shadow-violet-600/30">
                          <Layers className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold tracking-tight text-foreground">Build your own OS</p>
                          <p className="text-[11.5px] font-normal leading-snug text-muted-foreground">Mix base + add-ons, see price live.</p>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 flex-none text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
                      </Link>

                      <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-normal text-muted-foreground">
                        <span>Cancel anytime</span>
                        <span className="h-1 w-1 rounded-full bg-border" />
                        <span>No setup fees</span>
                        <span className="h-1 w-1 rounded-full bg-border" />
                        <span>&lt; 14d go-live</span>
                      </p>
                    </div>

                    {/* COL 2 — Add-ons (colorful) */}
                    <div className="col-span-4 border-r border-border/70 p-5">
                      <div className="mb-3 flex items-baseline justify-between">
                        <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
                          Snap-on add-ons
                        </p>
                        <span className="text-[11px] font-medium text-muted-foreground/70">{ADDONS.length} modules</span>
                      </div>
                      <div className="grid grid-cols-1 gap-1">
                        {ADDONS.map((m) => {
                          const Icon = m.icon;
                          const isActive = location.pathname === `/products/${m.id}`;
                          const AI_MODULES = new Set(["call-iq-ai", "ai-workforce", "sales-autopilot", "clinic-pulse"]);
                          const isAI = AI_MODULES.has(m.id);
                          return (
                            <Link
                              key={m.id}
                              to={`/products/${m.id}`}
                              data-testid={`products-dropdown-${m.id}`}
                              onClick={() => setProductsOpen(false)}
                              className={`group flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all hover:-translate-y-0.5 hover:border-border hover:bg-white hover:shadow-sm dark:hover:bg-white/[0.04] ${
                                isActive ? "border-foreground/30 bg-secondary/60" : "border-transparent"
                              }`}
                            >
                              <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${m.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                                <Icon className="h-[15px] w-[15px]" />
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="truncate text-[12.5px] font-semibold tracking-tight text-foreground">{m.name}</span>
                                  {isAI && (
                                    <span className="rounded bg-gradient-to-r from-violet-500 to-fuchsia-500 px-1 py-px text-[8.5px] font-bold uppercase tracking-[0.06em] text-white shadow-sm">
                                      AI
                                    </span>
                                  )}
                                  <ArrowRight className="ml-auto h-3 w-3 flex-none text-foreground/35 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                                </div>
                                <p className="truncate text-[11.5px] font-normal leading-snug text-muted-foreground">
                                  {m.tagline}
                                </p>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>

                    {/* COL 3 — Featured */}
                    <div className="col-span-3 flex flex-col p-5">
                      <p className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
                        Featured
                      </p>

                      {/* Audit lead-magnet card */}
                      <button
                        type="button"
                        data-testid="products-dropdown-featured-audit"
                        onClick={() => {
                          setProductsOpen(false);
                          openAudit();
                        }}
                        className="group relative w-full overflow-hidden rounded-xl border border-teal-500/30 bg-gradient-to-br from-teal-500/[0.10] via-cyan-500/[0.05] to-transparent p-4 text-left transition-all hover:-translate-y-0.5 hover:border-teal-500/55 hover:shadow-md hover:shadow-teal-500/15"
                      >
                        <div className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-teal-500/20 blur-3xl" />
                        <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-sm shadow-teal-500/30">
                          <Sparkles className="h-4 w-4" />
                        </span>
                        <p className="relative mt-3 text-[13.5px] font-semibold leading-tight tracking-tight text-foreground">
                          Take the 60-second audit
                        </p>
                        <p className="relative mt-1 text-[11.5px] font-normal leading-snug text-muted-foreground">
                          See your score + monthly $ left on the table.
                        </p>
                        <span className="relative mt-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-teal-600 dark:text-teal-300">
                          Start now
                          <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </button>

                      {/* Integrations card */}
                      <Link
                        to="/integrations"
                        onClick={() => setProductsOpen(false)}
                        data-testid="products-dropdown-featured-integrations"
                        className="group mt-2.5 rounded-xl border border-border bg-secondary/30 p-4 transition-colors hover:border-foreground/25 hover:bg-secondary/60"
                      >
                        <div className="flex items-center gap-1.5">
                          {["juvonno.com", "jane.app"].map((d) => (
                            <span key={d} className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white ring-1 ring-inset ring-border">
                              <img loading="lazy" decoding="async"
                                src={`https://www.google.com/s2/favicons?domain=${d}&sz=128`}
                                alt=""
                                className="h-4 w-4 object-contain"
                              />
                            </span>
                          ))}
                          <span className="inline-flex h-7 items-center justify-center rounded-md bg-secondary px-1.5 text-[10.5px] font-semibold text-foreground/70 ring-1 ring-inset ring-border">
                            +13
                          </span>
                        </div>
                        <p className="mt-3 text-[13.5px] font-semibold tracking-tight text-foreground">Integrates with your stack</p>
                        <p className="mt-1 text-[11.5px] font-normal leading-snug text-muted-foreground">
                          Juvonno · Jane · Shopify · Twilio · Google Calendar.
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground/85">
                          See all integrations
                          <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </Link>
                    </div>
                  </div>

                  {/* Footer bar — "Built for your specialty" */}
                  <div className="flex flex-wrap items-center justify-between gap-y-2 border-t border-border/70 bg-secondary/40 px-6 py-2.5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">Built for</span>
                      <div className="flex flex-wrap items-center gap-1">
                        {[
                          { slug: "physiotherapy", label: "Physio" },
                          { slug: "chiropractic",  label: "Chiro" },
                          { slug: "dental",        label: "Dental" },
                          { slug: "spa",           label: "Spa" },
                          { slug: "dietetics",     label: "Dietetics" },
                        ].map((d) => (
                          <Link
                            key={d.slug}
                            to={`/disciplines/${d.slug}`}
                            onClick={() => setProductsOpen(false)}
                            data-testid={`products-dropdown-built-${d.slug}`}
                            className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-0.5 text-[11.5px] font-semibold tracking-tight text-foreground transition-colors hover:border-foreground/40 hover:bg-background"
                          >
                            {d.label}
                          </Link>
                        ))}
                        <Link
                          to="/platform"
                          onClick={() => setProductsOpen(false)}
                          data-testid="products-dropdown-built-more"
                          className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
                        >
                          + more
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setProductsOpen(false); handleBookDemo(); }}
                      data-testid="products-dropdown-demo"
                      className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1 text-[11.5px] font-medium text-background transition-colors hover:bg-foreground/90"
                    >
                      <CalendarCheck className="h-3 w-3" />
                      Book a Demo
                    </button>
                  </div>
                </motion.div>
              )}
              </AnimatePresence>
            </div>

            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}

            {/* New-visitor self-guided tour entry point */}
            <NavLink
              to={NEW_VISITOR_LINK.to}
              data-testid="nav-new-here"
              className={({ isActive }) =>
                `ml-1 inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "border-violet-500/40 bg-violet-500/15 text-foreground"
                    : "border-violet-500/30 bg-violet-500/[0.06] text-violet-700 hover:border-violet-500/55 hover:bg-violet-500/15 dark:text-violet-300"
                }`
              }
            >
              <Sparkles className="h-3.5 w-3.5" />
              {NEW_VISITOR_LINK.label}
            </NavLink>

            {/* Meet Clinic OS — opens the long-form Join Clinic OS page */}
            <NavLink
              to={MEET_CLINICOS_LINK.to}
              data-testid="nav-meet-clinicos"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "border-emerald-500/40 bg-emerald-500/15 text-foreground"
                    : "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-700 hover:border-emerald-500/55 hover:bg-emerald-500/15 dark:text-emerald-300"
                }`
              }
            >
              <BookOpen className="h-3.5 w-3.5" />
              {MEET_CLINICOS_LINK.label}
            </NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              data-testid="header-book-demo"
              onClick={handleBookDemo}
              className="hidden h-9 items-center gap-1.5 rounded-md bg-gradient-to-r from-blue-600 to-violet-600 px-4 text-sm font-semibold text-white shadow-md shadow-violet-600/25 transition-all hover:-translate-y-0.5 hover:shadow-lg lg:inline-flex"
            >
              <CalendarCheck className="h-3.5 w-3.5" />
              Book a Demo
            </button>
            <button
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-secondary/40"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border/60 bg-background shadow-2xl">
          <div className="max-h-[calc(100vh-4rem)] overflow-y-auto px-5 py-5">
            {/* Primary nav links — Products is an inline accordion */}
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => setMobileProductsOpen((v) => !v)}
                data-testid="mobile-nav-products"
                aria-expanded={mobileProductsOpen}
                className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left font-display text-[15px] font-bold tracking-tight transition-colors hover:bg-secondary"
              >
                <span className="inline-flex items-center gap-2">
                  <Layers className="h-4 w-4 text-violet-500" />
                  Products
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${mobileProductsOpen ? "rotate-180" : ""}`} />
              </button>

              {mobileProductsOpen && (
                <div data-testid="mobile-products-panel" className="mb-2 mt-1 space-y-3 rounded-xl border border-border bg-secondary/30 p-3">
                  {/* Bases */}
                  <div>
                    <p className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Bases</p>
                    <div className="mt-1.5 grid grid-cols-1 gap-1.5">
                      {BASES.map((b) => (
                        <Link
                          key={b.id}
                          to={`/products/${b.id}`}
                          onClick={() => { setMobileProductsOpen(false); setOpen(false); }}
                          data-testid={`mobile-products-base-${b.id}`}
                          className="group flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:border-foreground/25"
                        >
                          <span className="font-display text-[13.5px] font-black tracking-tight">{b.name}</span>
                          <span className="text-[11px] font-semibold text-muted-foreground">${b.price}<span className="text-[10px]">/mo</span></span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Add-ons */}
                  <div>
                    <div className="flex items-center justify-between px-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Add-ons</p>
                      <span className="text-[10px] font-medium text-muted-foreground/70">{ADDONS.length} modules</span>
                    </div>
                    <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                      {ADDONS.map((m) => (
                        <Link
                          key={m.id}
                          to={`/products/${m.id}`}
                          onClick={() => { setMobileProductsOpen(false); setOpen(false); }}
                          data-testid={`mobile-products-addon-${m.id}`}
                          className="rounded-lg border border-border bg-card px-2.5 py-2 text-[11.5px] font-semibold leading-tight text-foreground/85 transition-colors hover:border-foreground/25"
                        >
                          {m.name}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <Link
                    to="/build"
                    onClick={() => { setMobileProductsOpen(false); setOpen(false); }}
                    data-testid="mobile-products-build"
                    className="flex items-center justify-between rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 px-3 py-2.5 font-display text-[13px] font-bold tracking-tight text-white shadow-sm"
                  >
                    <span>Build Your OS</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}

              {NAV.map((n) => (
                <NavLink
                  key={n.label}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-lg px-3 py-3 font-display text-[15px] font-bold tracking-tight transition-colors hover:bg-secondary"
                >
                  {n.label}
                  <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-muted-foreground" />
                </NavLink>
              ))}
              <NavLink
                to={NEW_VISITOR_LINK.to}
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-new-here"
                className="mt-1 flex items-center justify-between rounded-lg border border-violet-500/30 bg-violet-500/[0.07] px-3 py-3 font-display text-[15px] font-bold tracking-tight text-violet-700 transition-colors hover:bg-violet-500/15 dark:text-violet-300"
              >
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  {NEW_VISITOR_LINK.label}
                </span>
                <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-muted-foreground" />
              </NavLink>
              <NavLink
                to={MEET_CLINICOS_LINK.to}
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-meet-clinicos"
                className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/[0.07] px-3 py-3 font-display text-[15px] font-bold tracking-tight text-emerald-700 transition-colors hover:bg-emerald-500/15 dark:text-emerald-300"
              >
                <span className="inline-flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  {MEET_CLINICOS_LINK.label}
                </span>
                <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-muted-foreground" />
              </NavLink>
            </div>

            {/* Footer line — book-a-demo + audit removed; both live on the sticky bottom CTA */}
            <p className="mt-6 px-1 text-[10.5px] text-muted-foreground">
              Book a demo &amp; Free audit are always one tap away in the bottom bar.
            </p>
          </div>
        </div>
      )}
    </header>
  );
};
